import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Send, Mail, Check, X, Loader, Trash2, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { useTheme } from '../../context/ThemeContext';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const BILLING_OPTS = [
  { key: 'monthly', label: '1 Month' },
  { key: '3mo',     label: '3 Months' },
  { key: '6mo',     label: '6 Months' },
  { key: '1yr',     label: '1 Year'   },
];

const PLAN_TOTAL = {
  premium: { monthly: 599, '3mo': 1499, '6mo': 2699, '1yr': 4799 },
};

function InviteSection({ onBack }) {
  const { colors } = useTheme();
  const ac = colors.accent;

  const [email,       setEmail]       = useState('');
  const [discount,    setDiscount]    = useState(90);
  const [tier,        setTier]        = useState('premium');
  const [billing,     setBilling]     = useState('3mo');
  const [expires,     setExpires]     = useState(7);
  const [loading,     setLoading]     = useState(false);
  const [success,     setSuccess]     = useState('');
  const [error,       setError]       = useState('');
  const [invites,     setInvites]     = useState([]);
  const [listLoading, setListLoading] = useState(true);

  const original   = PLAN_TOTAL[tier]?.[billing] ?? 600;
  const discounted = Math.max(1, Math.round(original * (100 - discount) / 100));

  const loadInvites = useCallback(async () => {
    setListLoading(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API_URL}/admin/invites`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInvites(data);
    } catch { /* silent */ }
    finally { setListLoading(false); }
  }, []);

  useEffect(() => { loadInvites(); }, [loadInvites]);

  const handleSend = async () => {
    if (!email.trim()) { setError('Enter an email address'); return; }
    setError(''); setSuccess(''); setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(
        `${API_URL}/admin/invites`,
        { email: email.trim(), discount_pct: discount, applies_to: tier, applies_to_billing: billing, expires_days: expires },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSuccess(`Invite sent — code ${data.code}`);
      setEmail('');
      loadInvites();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to send invite');
    } finally { setLoading(false); }
  };

  const handleDeactivate = async (code) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/admin/invites/${code}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      loadInvites();
    } catch { /* silent */ }
  };

  const inputStyle = {
    background: colors.inputBg || '#0f0f0f',
    border: `1px solid ${colors.border}`,
    borderRadius: 10,
    color: colors.primaryText,
    fontSize: 13,
    padding: '10px 12px',
    width: '100%',
    outline: 'none',
  };

  const segStyle = (active) => ({
    flex: 1,
    padding: '7px 0',
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    border: 'none',
    background: active ? ac : 'transparent',
    color: active ? '#fff' : colors.secondaryText,
    transition: 'background 0.15s',
  });

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="p-1.5 rounded-md" style={{ backgroundColor: colors.cardBg, border: `1px solid ${colors.border}` }}>
          <ArrowLeft className="w-4 h-4" style={{ color: colors.primaryText }} />
        </button>
        <h1 className="text-[17px] font-bold" style={{ color: colors.primaryText }}>Send Invite</h1>
      </div>

      {/* Compose form */}
      <div className="rounded-xl p-4 space-y-4" style={{ background: colors.cardBg, border: `1px solid ${colors.border}` }}>
        <p className="text-[12px] font-semibold uppercase tracking-wider" style={{ color: colors.secondaryText }}>New Invite</p>

        {/* Email */}
        <input
          type="email"
          placeholder="user@email.com"
          value={email}
          onChange={e => { setEmail(e.target.value); setError(''); setSuccess(''); }}
          style={inputStyle}
        />

        {/* Tier */}
        <div>
          <p className="text-[11px] mb-1.5" style={{ color: colors.secondaryText }}>Plan</p>
          <div className="flex gap-1 p-1 rounded-xl" style={{ background: colors.inputBg || '#0f0f0f' }}>
            {['premium'].map(t => (
              <button key={t} style={segStyle(tier === t)} onClick={() => setTier(t)}>
                {t.charAt(0).toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Billing */}
        <div>
          <p className="text-[11px] mb-1.5" style={{ color: colors.secondaryText }}>Duration</p>
          <div className="flex gap-1 p-1 rounded-xl" style={{ background: colors.inputBg || '#0f0f0f' }}>
            {BILLING_OPTS.map(o => (
              <button key={o.key} style={segStyle(billing === o.key)} onClick={() => setBilling(o.key)}>
                {o.label}
              </button>
            ))}
          </div>
        </div>

        {/* Discount + Expiry row */}
        <div className="flex gap-3">
          <div style={{ flex: 1 }}>
            <p className="text-[11px] mb-1.5" style={{ color: colors.secondaryText }}>Discount %</p>
            <input
              type="number" min={1} max={99}
              value={discount}
              onChange={e => setDiscount(Math.min(99, Math.max(1, Number(e.target.value))))}
              style={{ ...inputStyle }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <p className="text-[11px] mb-1.5" style={{ color: colors.secondaryText }}>Expires (days)</p>
            <input
              type="number" min={1} max={90}
              value={expires}
              onChange={e => setExpires(Math.min(90, Math.max(1, Number(e.target.value))))}
              style={{ ...inputStyle }}
            />
          </div>
        </div>

        {/* Price preview */}
        <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg" style={{ background: `${ac}10`, border: `1px solid ${ac}25` }}>
          <span className="text-[13px] font-bold" style={{ color: ac }}>₹{discounted}</span>
          <span className="text-[12px] line-through" style={{ color: colors.secondaryText }}>₹{original}</span>
          <span className="text-[12px]" style={{ color: colors.secondaryText }}>· {discount}% off · saves ₹{original - discounted}</span>
        </div>

        {error   && <p className="text-[12px]" style={{ color: '#ef4444' }}>{error}</p>}
        {success && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ background: 'rgba(22,199,132,0.08)', border: '1px solid rgba(22,199,132,0.25)' }}>
            <Check className="w-3.5 h-3.5" style={{ color: '#16C784' }} />
            <span className="text-[12px] font-semibold" style={{ color: '#16C784' }}>{success}</span>
          </div>
        )}

        <button
          onClick={handleSend}
          disabled={loading || !email.trim()}
          className="w-full py-2.5 rounded-xl text-[14px] font-semibold flex items-center justify-center gap-2"
          style={{ background: ac, color: '#fff', opacity: (!email.trim() || loading) ? 0.5 : 1 }}
        >
          {loading
            ? <Loader className="w-4 h-4 animate-spin" />
            : <><Send className="w-3.5 h-3.5" /> Send Invite</>}
        </button>
      </div>

      {/* Sent invites list */}
      <div className="rounded-xl p-4 space-y-3" style={{ background: colors.cardBg, border: `1px solid ${colors.border}` }}>
        <div className="flex items-center justify-between">
          <p className="text-[12px] font-semibold uppercase tracking-wider" style={{ color: colors.secondaryText }}>Sent Invites</p>
          <button onClick={loadInvites} className="p-1 rounded-md" style={{ background: 'transparent' }}>
            <RefreshCw className="w-3.5 h-3.5" style={{ color: colors.secondaryText }} />
          </button>
        </div>

        {listLoading ? (
          <div className="flex justify-center py-4">
            <Loader className="w-4 h-4 animate-spin" style={{ color: colors.secondaryText }} />
          </div>
        ) : invites.length === 0 ? (
          <p className="text-[13px] text-center py-3" style={{ color: colors.secondaryText }}>No invites sent yet</p>
        ) : (
          <div className="space-y-2">
            {invites.map(inv => {
              const used    = inv.uses_count > 0;
              const expired = inv.valid_until && new Date(inv.valid_until) < new Date();
              const active  = inv.is_active && !expired && !used;
              return (
                <div key={inv.code} className="flex items-center justify-between px-3 py-2.5 rounded-lg"
                  style={{ background: colors.background, border: `1px solid ${colors.border}` }}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Mail className="w-3 h-3 flex-shrink-0" style={{ color: colors.secondaryText }} />
                      <span className="text-[12px] truncate" style={{ color: colors.primaryText }}>{inv.restricted_to_email}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className="text-[11px] font-mono font-semibold" style={{ color: ac }}>{inv.code}</span>
                      <span className="text-[11px]" style={{ color: colors.secondaryText }}>{inv.discount_pct}% off · {inv.applies_to} {inv.applies_to_billing || ''}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold"
                        style={{
                          background: used ? 'rgba(22,199,132,0.12)' : expired ? 'rgba(239,68,68,0.12)' : active ? `${ac}15` : 'rgba(107,114,128,0.12)',
                          color:      used ? '#16C784'               : expired ? '#ef4444'               : active ? ac         : '#6b7280',
                        }}>
                        {used ? 'Used' : expired ? 'Expired' : active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                  {active && (
                    <button onClick={() => handleDeactivate(inv.code)} className="ml-3 p-1.5 rounded-lg flex-shrink-0"
                      style={{ background: 'rgba(239,68,68,0.1)' }}>
                      <Trash2 className="w-3.5 h-3.5" style={{ color: '#ef4444' }} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default InviteSection;
